import { useMemo } from 'react';
import { useTribalContext } from '../context/tribal';
import {
  cubicCapacityToLitres,
  formatSpec,
  getEmissionsConsumptionValue,
  specIsInvalid,
} from '../modules/utils/storeUtils';
import { GlobalStoreStateConfiguration } from '../stores/globalStore';
import {
  fuelType,
  getDisplayFuelTypeName,
  normalizeFuelTypeName,
} from '../modules/constants/fuelType';
import { formatCurrency } from '../modules/utils/currency';
import { filterUnique } from '../modules/utils/array';

interface useConfigurationSpecsProps {
  configuration: GlobalStoreStateConfiguration;
  invalidSpecFallback: string;
}

export const useConfigurationSpecs = (props: useConfigurationSpecsProps) => {
  const { configuration, invalidSpecFallback } = props;
  const { contentManager } = useTribalContext();

  // Determine if stock or config car
  const key =
    configuration.vin || configuration.orderNumber ? 'STOCK' : 'CONFIGURATION';
  const isStock = key === 'STOCK';

  type SpecRow = { label: string; text: string };
  const dropEmptyForStock = (rows: SpecRow[]): SpecRow[] =>
    isStock
      ? rows.filter((row) => row.text && row.text !== invalidSpecFallback)
      : rows;

  // ---- SAFE SECTION EXTRACTION ----
  // `|| {}` catches BOTH null and undefined (unlike destructuring defaults)
  const technicalSpecifications =
    (configuration as any)?.pdbData?.technicalSpecifications || {};
  const pricing = (configuration as any)?.pdbData?.pricing || {};

  const engine = technicalSpecifications.engine || {};
  const transmission = technicalSpecifications.transmission || {};
  const performance = technicalSpecifications.performance || {};
  const dimensions = technicalSpecifications.dimensions || {};
  const loading = technicalSpecifications.loading || {};
  const battery = technicalSpecifications.battery || {};

  // Now these destructures are always safe, since each source is guaranteed an object
  const { fuelTypeName: rawFuelTypeName, cubicCapacity } = engine;
  const fuelTypeName = normalizeFuelTypeName(rawFuelTypeName);
  const { driveTypeName, gearboxTypeName, numberOfGears } = transmission;
  const { acceleration, maxSpeed, power, torqueLb, torqueRpm } = performance;
  const {
    width,
    height,
    length,
    numberOfDoors,
    bootVolume,
    bodyStyleName,
  } = dimensions;
  const { frontAxleLoad, rearAxleLoad, towbarLoad, unbraked, braked12 } =
    loading;
  const { capacity: technicalBatteryCapacity } = battery;
  // VIN PDBs omit the battery block. Stock derivative extra carries it.
  const stockBatteryCapacity = (configuration as GlobalStoreStateConfiguration)
    .stockData?.derivativeExtra?.battery?.capacity;
  const batteryCapacity = technicalBatteryCapacity ?? stockBatteryCapacity;
  const modelYear = (configuration as GlobalStoreStateConfiguration).pdbData
    ?.derivative?.year;
  const { abiInsurance50 } = technicalSpecifications;
  const { firstYearFee } = pricing;
  // ---- END SAFE SECTION EXTRACTION ----

  const getFuelTypeCharacter = () => {
    if (fuelTypeName === fuelType.ELECTRIC) {
      return '*';
    }

    if (fuelTypeName === fuelType.PLUG_IN_HYBRID) {
      return '**';
    }

    if (
      fuelTypeName === fuelType.HYBRID ||
      fuelTypeName === fuelType.DIESEL ||
      fuelTypeName === fuelType.PETROL
    ) {
      return '^';
    }

    return '';
  };

  const consumptionCombined = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'combined'
  );

  const consumptionCombinedSustaining = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'combinedSustaining'
  );

  const consumptionExtraHigh = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'extraHigh'
  );

  const consumptionHigh = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'high'
  );

  const consumptionMedium = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'medium'
  );

  const consumptionLow = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'low'
  );

  const energyCombined = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'energyCombined'
  );

  const electricBatteryRange = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'batteryRangeCombined'
  );

  const electricBatteryRangeCity = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'batteryRangeCity'
  );
  const cityBatteryRangeValid =
    Number.isFinite(Number(electricBatteryRangeCity?.value)) &&
    Number(electricBatteryRangeCity?.value) > 0;

  const emissionsCombined = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'emissions',
    'combined'
  );

  const emissionsCombinedSustaining = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'emissions',
    'combinedSustaining'
  );

  const engineAndTransmissionSpecsElectric = [
    {
      label: contentManager.get('battery-capacity-label'),
      text: formatSpec(
        batteryCapacity,
        invalidSpecFallback,
        ' kWh',
        true
      ),
    },
  ];

  const engineAndTransmissionSpecsNonElectric = [
    {
      label: contentManager.get('engine-label'),
      text: formatSpec(
        cubicCapacityToLitres(cubicCapacity),
        invalidSpecFallback,
        ' litres'
      ),
    },
  ];

  const engineAndTransmissionSpecsDiesel = [
    {
      label: contentManager.get('adblue-label'),
      text: 'Yes',
    },
  ];

  const consumptionSpecsPetrolDieselHybrid = [
    {
      label: contentManager.get('combined-label'),
      text: formatSpec(
        consumptionCombined?.value,
        invalidSpecFallback,
        ` ${consumptionCombined?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('extra-high-label'),
      text: formatSpec(
        consumptionExtraHigh?.value,
        invalidSpecFallback,
        ` ${consumptionExtraHigh?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('high-label'),
      text: formatSpec(
        consumptionHigh?.value,
        invalidSpecFallback,
        ` ${consumptionHigh?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('medium-label'),
      text: formatSpec(
        consumptionMedium?.value,
        invalidSpecFallback,
        ` ${consumptionMedium?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('low-label'),
      text: formatSpec(
        consumptionLow?.value,
        invalidSpecFallback,
        ` ${consumptionLow?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
  ];

  const energyConsumptionSpecsElectric = [
    {
      label: contentManager.get('combined-label'),
      text: formatSpec(
        consumptionCombined?.value,
        invalidSpecFallback,
        ` ${consumptionCombined?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
  ];

  const batteryRangeSpecsElectric = [
    {
      label: contentManager.get('combined-label'),
      text: formatSpec(
        electricBatteryRange?.value,
        invalidSpecFallback,
        ` ${electricBatteryRange?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    ...(cityBatteryRangeValid
      ? [
          {
            label: contentManager.get('city-label'),
            text: formatSpec(
              electricBatteryRangeCity?.value,
              invalidSpecFallback,
              ` ${electricBatteryRangeCity?.unit}${getFuelTypeCharacter()}`,
              true
            ),
          },
        ]
      : []),
  ];

  const consumptionSpecsElectric = [
    {
      label: contentManager.get('energy-consumption-label'),
      text: formatSpec(
        consumptionCombined?.value,
        invalidSpecFallback,
        ` ${consumptionCombined?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('battery-range-label'),
      text: formatSpec(
        Math.round(electricBatteryRange?.value as number),
        invalidSpecFallback,
        ` ${electricBatteryRange?.unit}${getFuelTypeCharacter()}`,
        false
      ),
    },
    ...(cityBatteryRangeValid
      ? [
          {
            label: contentManager.get('battery-range-city-label'),
            text: formatSpec(
              Math.round(electricBatteryRangeCity?.value as number),
              invalidSpecFallback,
              ` ${electricBatteryRangeCity?.unit}${getFuelTypeCharacter()}`,
              false
            ),
          },
        ]
      : []),
    {
      label: contentManager.get('consumption-low'),
      text: formatSpec(
        consumptionLow?.value,
        invalidSpecFallback,
        ` ${consumptionLow?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('consumption-medium'),
      text: formatSpec(
        consumptionMedium?.value,
        invalidSpecFallback,
        ` ${consumptionMedium?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('consumption-high'),
      text: formatSpec(
        consumptionHigh?.value,
        invalidSpecFallback,
        ` ${consumptionHigh?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('consumption-extra-high'),
      text: formatSpec(
        consumptionExtraHigh?.value,
        invalidSpecFallback,
        ` ${consumptionExtraHigh?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
  ];

  const fuelConsumptionSpecsPlugInHybrid = (shortenedLabels?: boolean) => [
    {
      label: shortenedLabels
        ? contentManager.get('combined-label')
        : contentManager.get('petrol-diesel-combined-label'),
      text: formatSpec(
        consumptionCombined?.value,
        invalidSpecFallback,
        ` ${consumptionCombined?.unit}${getFuelTypeCharacter()} (W)`,
        true
      ),
    },
    {
      label: shortenedLabels
        ? contentManager.get('combined-label')
        : contentManager.get('petrol-diesel-combined-label'),
      text: formatSpec(
        consumptionCombinedSustaining?.value,
        invalidSpecFallback,
        ` ${consumptionCombinedSustaining?.unit}${getFuelTypeCharacter()} (S)`,
        true
      ),
    },
    {
      label: shortenedLabels
        ? contentManager.get('extra-high-label')
        : contentManager.get('petrol-diesel-extra-high-label'),
      text: formatSpec(
        consumptionExtraHigh?.value,
        invalidSpecFallback,
        ` ${consumptionExtraHigh?.unit}${getFuelTypeCharacter()} (S)`,
        true
      ),
    },
    {
      label: shortenedLabels
        ? contentManager.get('high-label')
        : contentManager.get('petrol-diesel-high-label'),
      text: formatSpec(
        consumptionHigh?.value,
        invalidSpecFallback,
        ` ${consumptionHigh?.unit}${getFuelTypeCharacter()} (S)`,
        true
      ),
    },
    {
      label: shortenedLabels
        ? contentManager.get('medium-label')
        : contentManager.get('petrol-diesel-medium-label'),
      text: formatSpec(
        consumptionMedium?.value,
        invalidSpecFallback,
        ` ${consumptionMedium?.unit}${getFuelTypeCharacter()} (S)`,
        true
      ),
    },
    {
      label: shortenedLabels
        ? contentManager.get('low-label')
        : contentManager.get('petrol-diesel-low-label'),
      text: formatSpec(
        consumptionLow?.value,
        invalidSpecFallback,
        ` ${consumptionLow?.unit}${getFuelTypeCharacter()} (S)`,
        true
      ),
    },
  ];

  const energyConsumptionSpecsPlugInHybrid = [
    {
      label: contentManager.get('combined-label'),
      text: formatSpec(
        energyCombined?.value,
        invalidSpecFallback,
        ` ${energyCombined?.unit}${getFuelTypeCharacter()} (W)`,
        true
      ),
    },
  ];

  const consumptionSpecsPlugInHybrid = [
    ...fuelConsumptionSpecsPlugInHybrid(),
    {
      label: contentManager.get('energy-combined-label'),
      text: formatSpec(
        energyCombined?.value,
        invalidSpecFallback,
        ` ${energyCombined?.unit}${getFuelTypeCharacter()} (W)`,
        true
      ),
    },
    ...(cityBatteryRangeValid
      ? [
          {
            label: contentManager.get('battery-range-city-label'),
            text: formatSpec(
              Math.round(electricBatteryRangeCity?.value as number),
              invalidSpecFallback,
              ` ${electricBatteryRangeCity?.unit}${getFuelTypeCharacter()}`,
              false
            ),
          },
        ]
      : []),
    {
      label: contentManager.get('battery-range-label'),
      text: formatSpec(
        Math.round(electricBatteryRange?.value as number),
        invalidSpecFallback,
        ` ${electricBatteryRange?.unit}${getFuelTypeCharacter()}`,
        false
      ),
    },
  ];

  const emissionsSpecsPetrolDieselHybridElectric = [
    {
      label: contentManager.get('emissions-label'),
      text: formatSpec(
        emissionsCombined?.value,
        invalidSpecFallback,
        ` ${emissionsCombined?.unit}${getFuelTypeCharacter()}`,
        false
      ),
    },
  ];

  const emissionsSpecsPlugInHybrid = [
    {
      label: contentManager.get('emissions-label'),
      text: formatSpec(
        emissionsCombined?.value,
        invalidSpecFallback,
        ` ${emissionsCombined?.unit}${getFuelTypeCharacter()} (W)`,
        false
      ),
    },
    {
      label: contentManager.get('emissions-label'),
      text: formatSpec(
        emissionsCombinedSustaining?.value,
        invalidSpecFallback,
        ` ${emissionsCombinedSustaining?.unit}${getFuelTypeCharacter()} (S)`,
        false
      ),
    },
  ];

  const emissionsCombinedNonHybrid = [
    {
      label: contentManager.get('combined-label'),
      text: formatSpec(
        emissionsCombined?.value,
        invalidSpecFallback,
        ` ${emissionsCombined?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
  ];

  const emissionsCombinedPlugInHybrid = [
    {
      label: contentManager.get('combined-label'),
      text: filterUnique([
        formatSpec(
          emissionsCombined?.value,
          invalidSpecFallback,
          ` ${emissionsCombined?.unit}${getFuelTypeCharacter()} (W)`,
          false
        ),
        formatSpec(
          emissionsCombinedSustaining?.value,
          invalidSpecFallback,
          ` ${emissionsCombinedSustaining?.unit}${getFuelTypeCharacter()} (S)`,
          false
        ),
      ]).join(' '),
    },
  ];

  return useMemo(
    () => ({
      getEngineAndTransmissionSpecs: ({
        includeDoors = isStock,
      }: { includeDoors?: boolean } = {}) => {
        const transmissionText = specIsInvalid(numberOfGears)
          ? fuelTypeName === fuelType.ELECTRIC
            ? `1 speed ${gearboxTypeName}`
            : formatSpec(gearboxTypeName, invalidSpecFallback)
          : `${numberOfGears} speed ${gearboxTypeName}`;

        return dropEmptyForStock([
          {
            label: contentManager.get('fuel-type-label'),
            text: formatSpec(
              getDisplayFuelTypeName(fuelTypeName),
              invalidSpecFallback
            ),
          },
          {
            label: contentManager.get('gearbox-label'),
            text: formatSpec(gearboxTypeName, invalidSpecFallback),
          },
          ...(fuelTypeName === fuelType.ELECTRIC
            ? engineAndTransmissionSpecsElectric
            : engineAndTransmissionSpecsNonElectric),
          {
            label: contentManager.get('wheel-drive-label'),
            text: formatSpec(driveTypeName, invalidSpecFallback),
          },
          {
            label: contentManager.get('transmission-label'),
            text: transmissionText,
          },
          ...(fuelTypeName === fuelType.DIESEL
            ? engineAndTransmissionSpecsDiesel
            : []),
          ...(modelYear != null && `${modelYear}` !== ''
            ? [
                {
                  label: contentManager.get('model-year-label'),
                  text: formatSpec(modelYear, invalidSpecFallback),
                },
              ]
            : []),
          ...(includeDoors && numberOfDoors != null
            ? [
                {
                  label: contentManager.get('doors-engine-label'),
                  text: formatSpec(numberOfDoors, invalidSpecFallback),
                },
              ]
            : []),
        ]);
      },

      getPerformanceSpecs: () => {
        const powerSpec = {
          label: contentManager.get('maximum-power-label'),
          text: formatSpec(power, invalidSpecFallback, 'ps'),
        };

        if (isStock) {
          return dropEmptyForStock([powerSpec]);
        }

        return [
          {
            label: contentManager.get('acceleration-label'),
            text: formatSpec(acceleration, invalidSpecFallback, ' seconds'),
          },
          {
            label: contentManager.get('maximum-speed-label'),
            text: formatSpec(maxSpeed, invalidSpecFallback, 'mph'),
          },
          powerSpec,
          {
            label: contentManager.get('maximum-torque-label'),
            text:
              specIsInvalid(torqueLb) || specIsInvalid(torqueRpm)
                ? invalidSpecFallback
                : formatSpec(torqueLb, invalidSpecFallback, ' lbs.ft at ') +
                  formatSpec(torqueRpm, invalidSpecFallback, 'rpm'),
          },
        ];
      },

      getDimensionsSpecs: () => {
        if (isStock) return [];

        return [
          {
            label: contentManager.get('width-label'),
            text: formatSpec(width, invalidSpecFallback, 'mm'),
          },
          {
            label: contentManager.get('height-label'),
            text: formatSpec(height, invalidSpecFallback, 'mm'),
          },
          {
            label: contentManager.get('length-label'),
            text: formatSpec(length, invalidSpecFallback, 'mm'),
          },
          {
            label: contentManager.get('doors-label'),
            text: formatSpec(numberOfDoors, invalidSpecFallback),
          },
          {
            label: contentManager.get('boot-space-label'),
            text: formatSpec(bootVolume, invalidSpecFallback, ' litres'),
          },
          {
            label: contentManager.get('style-label'),
            text: formatSpec(bodyStyleName, invalidSpecFallback),
          },
        ];
      },

      getLoadingSpecs: () => {
        if (isStock) return [];

        return [
          {
            label: 'Front axle load limit (kg)',
            text: formatSpec(frontAxleLoad, invalidSpecFallback, ' kg'),
          },
          {
            label: 'Rear axle load limit (kg)',
            text: formatSpec(rearAxleLoad, invalidSpecFallback, ' kg'),
          },
          {
            label: 'Towbar load limit (kg)',
            text: formatSpec(towbarLoad, invalidSpecFallback, ' kg'),
          },
          {
            label: 'Trailer load limit – Unbraked (kg)',
            text: formatSpec(unbraked, invalidSpecFallback, ' kg'),
          },
          {
            label: 'Trailer load limit – Braked 12% incline (kg)',
            text: formatSpec(braked12, invalidSpecFallback, ' kg'),
          },
        ];
      },

      getConsumptionSpecs: () => {
        return dropEmptyForStock([
          ...(fuelTypeName === fuelType.PETROL ||
          fuelTypeName === fuelType.DIESEL ||
          fuelTypeName === fuelType.HYBRID
            ? consumptionSpecsPetrolDieselHybrid
            : []),
          ...(fuelTypeName === fuelType.ELECTRIC
            ? consumptionSpecsElectric
            : []),
          ...(fuelTypeName === fuelType.PLUG_IN_HYBRID
            ? consumptionSpecsPlugInHybrid
            : []),
        ]);
      },

      getEmissionsSpecs: () => {
        return dropEmptyForStock([
          ...(fuelTypeName === fuelType.PLUG_IN_HYBRID
            ? emissionsSpecsPlugInHybrid
            : emissionsSpecsPetrolDieselHybridElectric),
        ]);
      },

      getTaxAndInsuranceSpecs: () => {
        const vedSpec = {
          label: contentManager.get('ved-label'),
          text: formatSpec(formatCurrency(firstYearFee), invalidSpecFallback),
        };

        if (isStock) {
          return dropEmptyForStock([vedSpec]);
        }

        return [
          {
            label: contentManager.get('insurance-group-label'),
            text: formatSpec(abiInsurance50, invalidSpecFallback),
          },
          vedSpec,
        ];
      },

      getFuelConsumptionSpecs: () => {
        return dropEmptyForStock([
          ...(fuelTypeName === fuelType.PETROL ||
          fuelTypeName === fuelType.DIESEL ||
          fuelTypeName === fuelType.HYBRID
            ? consumptionSpecsPetrolDieselHybrid
            : []),
          ...(fuelTypeName === fuelType.PLUG_IN_HYBRID
            ? fuelConsumptionSpecsPlugInHybrid(true)
            : []),
        ]);
      },

      getEnergyConsumptionSpecs: () => {
        return dropEmptyForStock([
          ...(fuelTypeName === fuelType.ELECTRIC
            ? energyConsumptionSpecsElectric
            : fuelTypeName === fuelType.PLUG_IN_HYBRID
            ? energyConsumptionSpecsPlugInHybrid
            : []),
        ]);
      },

      getBatteryRangeSpecs: () => {
        return dropEmptyForStock([
          ...(fuelTypeName === fuelType.ELECTRIC
            ? batteryRangeSpecsElectric
            : []),
        ]);
      },

      getEmissionsCombinedSpecs: () => {
        return dropEmptyForStock([
          ...(fuelTypeName === fuelType.PLUG_IN_HYBRID
            ? emissionsCombinedPlugInHybrid
            : emissionsCombinedNonHybrid),
        ]);
      },
    }),
    []
  );
};